import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faGlobe } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { outputLanguageOptions } from "@/constants/outputLanguages";

import { User } from "@/pages/Public/pages/Login/schema/login";

const languageName = (code: string): string =>
	String(outputLanguageOptions.find((option) => option.value === code)?.title ?? code);

interface UserLanguagesCellProps {
	user: User;
}

/**
 * The sender's AI copywriting languages, as a hover (PREC-3686). Read-only by
 * design: a user manages their own languages in Personal Settings, so this only
 * answers "what will this sender's drafts be written in?" for an admin.
 */
export const UserLanguagesCell: FunctionComponent<UserLanguagesCellProps> = ({ user }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`users.languages.${key}`);

	const languages = user.copywritingLanguages;

	// A user who never set a preference has nothing to show — and the resolution
	// then falls through to the company default, which is not this column's story.
	if (!languages?.primaryLanguage && !languages?.fallbackLanguage) {
		return <span className="text-sm text-text-meta">—</span>;
	}

	const secondary = languages.secondaryLanguages.map(languageName).join(", ");

	return (
		<Tooltip
			content={
				<div className="flex flex-col gap-1.5">
					<span className="text-[10.5px] font-bold uppercase tracking-wide text-text-meta">
						{ts("tooltipTitle")}
					</span>
					<LanguageRow
						label={ts("primary")}
						value={languages.primaryLanguage ? languageName(languages.primaryLanguage) : null}
					/>
					<LanguageRow label={ts("secondary")} value={secondary || null} />
					<LanguageRow
						label={ts("fallback")}
						value={languages.fallbackLanguage ? languageName(languages.fallbackLanguage) : null}
					/>
				</div>
			}
			direction="top"
		>
			<span className="inline-flex w-fit cursor-default items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 duration-200 hover:border-brand-light hover:text-brand-light">
				<Icon className="h-3 w-3" icon={faGlobe} />
				{ts("label")}
			</span>
		</Tooltip>
	);
};

interface LanguageRowProps {
	label: string;
	value: string | null;
}

const LanguageRow: FunctionComponent<LanguageRowProps> = ({ label, value }) => {
	const { t } = useTranslation();

	return (
		<div className="flex items-baseline gap-2 border-t border-gray-100 pt-1.5 first:border-t-0 first:pt-0">
			<span className="w-[62px] flex-shrink-0 text-xs text-text-meta">{label}</span>
			<span
				className={value ? "text-xs font-semibold text-text" : "text-xs text-text-meta opacity-70"}
			>
				{value ?? t("users.languages.none")}
			</span>
		</div>
	);
};
